/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { isEqual } from "lodash";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppContext } from "@/app/context";
import { useDemoData } from "@/app/hooks";
import { ModalContext } from "@/app/modal-provider";
import { useAccountLinkedRegistriesQuery } from "@/app/types/generated/graphql";

import { TraceXRegistriesList, UseLinkedRegistries } from "./linked-registries.types";

export const useLinkedRegistries = (): UseLinkedRegistries => {
  const { t } = useTranslation();
  const { userAccountInfo, setUserAccountInfo } = useAppContext();
  const isUsingDemoData = useDemoData();

  const { data, loading, refetch } = useAccountLinkedRegistriesQuery({
    fetchPolicy: isUsingDemoData ? "cache-first" : "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (!userAccountInfo || isEqual(userAccountInfo?.linkedRegistries, data.account?.linkedRegistries)) return;

      setUserAccountInfo({
        ...userAccountInfo,
        linkedRegistries: data.account?.linkedRegistries,
      });
    },
  });
  const linkedRegistries = useMemo(() => data?.account?.linkedRegistries ?? [], [data?.account?.linkedRegistries]);

  const { showRegistryModal } = useContext(ModalContext);
  const linkedRegistryText = useMemo(() => {
    if (linkedRegistries.length === 0) return t("Your registry is not linked");
    return linkedRegistries?.length > 1 ? t("Your linked registries") : t("Your linked registry");
  }, [linkedRegistries, t]);

  const canLinkRegistry = linkedRegistries.length < Object.values(TraceXRegistriesList).length;

  const refetchRegistries = async () => {
    await refetch();
  };

  return {
    refetchRegistries,
    canLinkRegistry,
    loading,
    linkedRegistries,
    showRegistryModal,
    linkedRegistryText,
  };
};
